import { webAppUrl } from '@ecomm/data-env-variables'
import {
  AIChatNav,
  CardboardBox,
  Cart,
  HeadsetPerson,
  HelpCircle,
  HomeBase,
  MagicStick,
  OlympusCameraIcon,
  PencilAndRuler,
  Phone,
  Profile,
  Service,
  SimpliSafeAppIcon
} from '@ecomm/shared-icons'
import type { Locale } from '@ecomm/utils/locale/types'
import { CallSpecialistSideNavContent } from './CallSpecialistSideNavContent'

// Minimum width in pixels needed to display header in desktop mode. See https://www.figma.com/file/qITFbwWanfaVjL3Vo4A0vC/
export const NARROWEST_DESKTOP_SIZE = 1126

const PHONE_NUMBER_SPECIALIST = (specialistPhoneNumber = '1-833-838-3556') => ({
  'en-US': {
    residential: specialistPhoneNumber,
    business: '1-888-599-1726',
    available: 'Available 8am-10pm, ET'
  },
  'en-GB': {
    residential: '0800-456-1789',
    business: '0800-456-1789',
    available: 'Available 9am-7pm, UK'
  }
})
export const getMainMenuItems = (
  locale: Locale,
  isCameraOnlyVariant = false,
  isBusiness = false
) => [
  ...(isBusiness
    ? []
    : [
        {
          hasDropdown: true,
          dropdownMenu: 'PackagesDropdownMenu',
          text: locale === 'en-US' ? 'Shop Packages' : 'Shop',
          mobileIcon: <CardboardBox className="h-6 w-6" />
        }
      ]),
  {
    text: 'Build My System',
    url: isBusiness ? '/business/build-my-system' : '/build-my-system',
    mobileIcon: <PencilAndRuler className="h-6 w-6" />
  },
  isCameraOnlyVariant
    ? {
        text: 'Shop Camera Package',
        mobileIcon: <OlympusCameraIcon className="h-[29px] w-[29px]" />,
        url: '/outdoor-security-camera-bundle'
      }
    : {
        text: locale === 'en-US' ? 'Take Our Quiz' : 'Get A Quote',
        mobileIcon: <MagicStick className="h-[29px] w-[29px]" />,
        openQuiz: true,
        id: '1L1QlxR38aLrLL718EWGUh'
      },
  ...(!isBusiness
    ? [
        {
          text: 'Monitoring',
          url:
            locale === 'en-US'
              ? '/features-alarm-monitoring'
              : '/professional-alarm-monitoring',
          mobileIcon: <HeadsetPerson className="h-6 w-6" />
        }
      ]
    : [
        {
          text: 'Monitoring',
          url: '/business/features-alarm-monitoring',
          mobileIcon: <HeadsetPerson className="h-6 w-6" />
        }
      ])
]

export type CourtesyNavItemType = {
  readonly displayInSideNav?: boolean
  readonly icon?: React.ReactNode
  readonly hasDropdown?: boolean
  readonly dropdownMenu?: string
  readonly text: string
  readonly url?: string
  readonly mobileOnly?: boolean
  readonly openQuiz?: boolean
  readonly sideNavContent?: React.ReactNode
  readonly openAIChat?: boolean
}

export const cartItem: CourtesyNavItemType = {
  text: 'Cart',
  icon: <Cart className="h-6 w-6" />,
  url: '/cart'
}

export const phoneItem: CourtesyNavItemType = {
  text: 'Call',
  icon: <Phone className="h-6 w-6" />,
  mobileOnly: true
}

const supportItem = (
  locale: Locale,
  icon: React.ReactNode
): CourtesyNavItemType => ({
  displayInSideNav: true,
  hasDropdown: false,
  icon,
  text: 'Support',
  url: locale === 'en-US' ? 'https://support.simplisafe.com/' : '/contact-us'
})

const subMenuSupportItem = {
  text: 'Support',
  icon: <Service className="h-8 w-8" />,
  url: 'https://support.simplisafe.com/'
}

export const getCourtesyNavItems = (
  locale: Locale,
  areExpertsAvailable: boolean,
  isAIChatEnabled: boolean,
  isCameraOnlyVariant = false,
  isBusiness = false
) => {
  const phoneType = isBusiness ? 'business' : 'residential'
  const specialistPhoneNumber = isAIChatEnabled
    ? '1-833-725-3250'
    : '1-833-838-3556'
  const contact_phone = PHONE_NUMBER_SPECIALIST(specialistPhoneNumber)[locale][
    phoneType
  ]

  return [
    ...(isAIChatEnabled
      ? [
          {
            text: 'Ask AI',
            mobileOnly: false,
            displayInSideNav: true,
            icon: <AIChatNav className="h-6 w-6" />,
            openAIChat: true,
            sideNavContent: (
              <div className="mt-6 mb-3 flex justify-between w-full">
                <div>Ask AI</div>
                <AIChatNav className="h-5 w-5" />
              </div>
            )
          }
        ]
      : []),
    ...(isCameraOnlyVariant
      ? []
      : [
          {
            text: 'Take Quiz',
            icon: <MagicStick className="h-6 w-6" />,
            mobileOnly: true,
            openQuiz: true
          }
        ]),
    ...(areExpertsAvailable
      ? [
          {
            text: 'Call Sales',
            icon: <Phone className="h-6 w-6" />,
            mobileOnly: true,
            url: `tel:${contact_phone}`
          },
          {
            text: 'Call Sales',
            hasDropdown: true,
            dropdownMenu: 'CallSpecialistDropdownMenu',
            displayInSideNav: true,
            icon: <Phone className="h-6 w-6" />,
            url: `tel:${contact_phone}`,
            sideNavContent: <CallSpecialistSideNavContent />
          }
        ]
      : []),
    ...(isAIChatEnabled
      ? []
      : [supportItem(locale, <HelpCircle className="h-6 w-6" />)]),
    {
      displayInSideNav: true,
      hasDropdown: true,
      icon: <Profile className="mx-auto h-6 w-6" />,
      dropdownMenu: 'AccountDropdownMenu',
      text: 'Account'
    },
    cartItem
  ]
}

export type ServiceMenuItem = {
  readonly text: string
  readonly imageId: string
  readonly url: string
}

export const getServiceMenuItems = (
  locale: Locale
): readonly ServiceMenuItem[] => [
  ...(locale === 'en-US'
    ? []
    : [
        {
          text: 'Meet the System',
          imageId: '2n9okk7IQZPtmVYvSD3KXg',
          url: '/meet-the-system'
        }
      ]),
  {
    text:
      locale === 'en-US'
        ? 'Professional Monitoring'
        : '24/7 Professional Monitoring',
    imageId:
      locale === 'en-US' ? '2gnj0MqS2RvadjpecJDG6K' : '3X477mLg2byYfBD0MzmdMe',
    url:
      locale === 'en-US'
        ? '/features-alarm-monitoring'
        : '/professional-alarm-monitoring'
  },
  {
    text: 'Installation Options',
    imageId:
      locale === 'en-US' ? '75LoLEcNP7nJnbsRPpG3W6' : '4ISQo32VbzFoCuz0sPSEIb',
    url: '/installation'
  }
]

export const getPackagesMenuLeftItems = (locale: Locale) => [
  {
    text: 'Shop all packages',
    url: '/home-security-shop-packages'
  },
  ...(locale === 'en-US'
    ? [
        {
          text: 'Refurbished packages',
          url: '/refurb-home-security-systems-2'
        }
      ]
    : []),
  {
    caption: 'For existing customers:',
    text: 'Expand my system',
    url: '/alarm-sensors'
  }
]

export const getPackagesMenuMiddleItems = (locale: Locale) => [
  {
    title: 'Outdoor Elite Package',
    packageName: locale === 'en-US' ? 'The Beacon' : 'The Edinburgh',
    url:
      locale === 'en-US'
        ? '/home-security-system-beacon'
        : '/home-security-system-edinburgh',
    imageId:
      locale === 'en-US' ? '4bC09EpnddkbaJd0Rw1GS8' : '30qjwcrVYFr7ZLmfts9yID'
  },
  {
    title: 'Outdoor Basic Package',
    packageName: locale === 'en-US' ? 'The Lighthouse' : 'The Tower',
    url:
      locale === 'en-US'
        ? '/home-security-system-lighthouse'
        : '/home-security-system-tower',
    imageId:
      locale === 'en-US' ? '2S40uNCo4M8wvKgkKPQ17m' : '5C0JVOfwJLkJK6WF6NZ9PW'
  },
  {
    title: 'Indoor Basic Package',
    packageName: 'The Starter',
    url: '/home-security-system-starter',
    imageId: '6pToedl3jApJdlviDDSSFi'
  }
]

export const getPackageMenuRightItems = (locale: Locale) => ({
  title: locale === 'en-US' ? 'Customize A Package' : 'Customise A Package',
  description: 'Choose the cameras, sensors, and sirens that fit your needs',
  link: 'Build My System',
  url: '/build-my-system',
  imageId:
    locale === 'en-US' ? '2bbjRbJmiYWrDqmeIwqLpO' : '2H1lDKAlSNUC4E4nSolJIV'
})

export const accountMenuItems = (isAIChatEnabled = false) => [
  {
    text: 'Security dashboard',
    icon: (
      <SimpliSafeAppIcon className="h-8 w-8 rounded-md bg-white p-[2px] min-[1126px]:bg-transparent min-[1126px]:p-0" />
    ),
    url: webAppUrl()
  },
  ...(isAIChatEnabled ? [subMenuSupportItem] : []),
  {
    text: 'Expand my system',
    icon: <HomeBase className="h-8 w-8" />,
    url: '/alarm-sensors'
  }
]
